/**
 * Funciones tipadas por endpoint de /v1/*. Cada una es un envoltorio delgado
 * sobre `api`; ninguna contiene lógica de negocio (vive en el backend). Agrupadas
 * por módulo de dominio para que las pantallas lean como el mapa del API.
 */
import { api } from "./client.js";
import type {
  AdminStats,
  AdministrationOrder,
  BateriaAdminStats,
  BateriaCompletaDTO,
  BateriaDTO,
  BateriaGlobalStats,
  BateriaResumen,
  EstadoBateria,
  EstadoValidacion,
  FiltrosBateriaGlobal,
  ListaBaterias,
  ListaBateriasGlobal,
  LicenciasStats,
  NinoAdmin,
  PeffCatalogStats,
  UsuarioAdmin,
  Checkout,
  CompraIniciada,
  CodigoVinculo,
  Compartida,
  CorpusItemDetalle,
  CorpusTipoMedia,
  MediosDeItem,
  WordMediaItem,
  CorreccionItem,
  CorregirItemInput,
  ActualizarConfigInput,
  ConfigAdmin,
  EstadoCorpus,
  EstadoLicencia,
  EstadoSuscripcion,
  LicenciaAdminDetalle,
  ListaCorpus,
  ListaLicencias,
  ListaPagosOffline,
  PagoOffline,
  MetodoPagoOffline,
  EstadoPagoOffline,
  ProductoAdmin,
  CrearPlanInput,
  EditarPlanInput,
  DuplicarPlanInput,
  SaldoCreditos,
  LedgerCreditos,
  AsignacionCredito,
  ListaAsignaciones,
  JustificacionAsignacion,
  TipoBateriaCorpus,
  EjercicioDTO,
  EstadoConsentimientos,
  EstadoSuscripcionTerapeuta,
  Informe,
  IntentoResultado,
  InvitacionNino,
  ItemDTO,
  ModeloGrupo,
  MotivoSuspension,
  Nino,
  Palabra,
  PeffApoyo,
  PerfilConContacto,
  PerfilPropio,
  PerfilPublico,
  Plan,
  PlanCompleto,
  Posicion,
  Product,
  Prueba,
  PruebaConItems,
  Rol,
  PuntoSerieProgreso,
  RegistroItem,
  Reporte,
  SesionAuth,
  SesionPractica,
  SubidaAudio,
  SubidaFoto,
  TipoConsentimiento,
  TipoFoto,
  TipoPrueba,
  Usuario,
  Validacion,
  Variante,
  Verificacion,
  Vinculo,
  Workbench,
} from "./types.js";

// ---- auth -----------------------------------------------------------------
export const authApi = {
  registrar: (b: { email: string; password: string; nombre: string; rol: "padre" | "terapeuta" }) =>
    api.post<SesionAuth>("/v1/identity/auth/register", b, { auth: false }),
  login: (b: { email: string; password: string }) =>
    api.post<SesionAuth>("/v1/identity/auth/login", b, { auth: false }),
  logout: (refreshToken: string) =>
    api.post<void>("/v1/identity/auth/logout", { refreshToken }, { auth: false }),
};

// ---- niños + vínculos + consentimientos -----------------------------------
export const childrenApi = {
  listar: () => api.get<Nino[]>("/v1/identity/children"),
  obtener: (id: string) => api.get<Nino>(`/v1/identity/children/${id}`),
  crear: (b: { nombre: string; fechaNacimiento: string }) =>
    api.post<Nino>("/v1/identity/children", b),
  consentimientos: (childId: string) =>
    api.get<EstadoConsentimientos>(`/v1/identity/children/${childId}/consents`),
  otorgarConsentimiento: (childId: string, b: { tipo: TipoConsentimiento; versionTextoLegal: string }) =>
    api.post<unknown>(`/v1/identity/children/${childId}/consents`, b),
  // Terapeuta: crea paciente propio (sin padre). La autorización del tutor se
  // DECLARA en el mismo acto (declaracionConsentimiento) → consentimiento de audio.
  crearPaciente: (b: {
    nombre: string;
    fechaNacimiento: string;
    sexo?: "M" | "F" | "X" | null;
    notas?: string | null;
    declaracionConsentimiento: boolean;
  }) => api.post<Nino>("/v1/identity/patients", b),
  // Migración (banner): pacientes propios sin consentimiento + declaración en lote.
  consentPendientes: () =>
    api.get<{ total: number }>("/v1/identity/patients/consent-pendientes"),
  declararConsentimientoLote: () =>
    api.post<{ creados: number }>("/v1/identity/patients/consent-declarar"),
};

export const linkingApi = {
  listar: () => api.get<Vinculo[]>("/v1/identity/links"),
  crearCodigo: (childId: string) =>
    api.post<CodigoVinculo>(`/v1/identity/children/${childId}/link-code`),
  reclamarCodigo: (codigo: string) =>
    api.post<Vinculo>("/v1/identity/links/claim-code", { codigo }),
  invitar: (b: { nombre: string; fechaNacimiento: string; padreEmail: string }) =>
    api.post<InvitacionNino>("/v1/identity/invitations", b),
  reclamarInvitacion: (token: string) =>
    api.post<Vinculo>("/v1/identity/invitations/claim", { token }),
  activar: (id: string) => api.post<Vinculo>(`/v1/identity/links/${id}/activate`),
  revocar: (id: string) => api.post<Vinculo>(`/v1/identity/links/${id}/revoke`),
};

// ---- panel admin (herramienta interna; todo tras exigirRol admin) ---------
export const adminApi = {
  // identity
  stats: () => api.get<AdminStats>("/v1/identity/admin/stats"),
  usuarios: () => api.get<UsuarioAdmin[]>("/v1/identity/admin/users"),
  cambiarRol: (id: string, rol: Rol) =>
    api.patch<UsuarioAdmin>(`/v1/identity/admin/users/${id}/role`, { rol }),
  desactivar: (id: string) => api.post<UsuarioAdmin>(`/v1/identity/admin/users/${id}/deactivate`),
  reactivar: (id: string) => api.post<UsuarioAdmin>(`/v1/identity/admin/users/${id}/reactivate`),
  ninos: () => api.get<NinoAdmin[]>("/v1/identity/admin/children"),
  // assessment (evaluaciones)
  baterias: (q?: {
    estado?: EstadoBateria;
    banda?: "A" | "B" | "C" | "D";
    modo?: "prueba" | "normal";
    limit?: number;
    offset?: number;
  }) => api.get<ListaBaterias>("/v1/assessment/admin/baterias", { query: { ...q } }),
  bateriasStats: () => api.get<BateriaAdminStats>("/v1/assessment/admin/baterias/stats"),
  /** Reproceso administrativo de una batería atascada/inconcluyente. */
  reprocesar: (id: string) =>
    api.post<BateriaCompletaDTO>(`/v1/assessment/admin/baterias/${id}/reprocess`),
  // conteos para el dashboard (secciones completas llegan en el 2º PR)
  wordBankStats: () => api.get<PeffCatalogStats>("/v1/word-bank/admin/stats"),
  licenciasStats: () => api.get<LicenciasStats>("/v1/billing/admin/stats"),
  // Banco de Palabras — corpus PEFF SOLO LECTURA (regla de oro #3)
  wordBankCorpus: (q?: {
    tipoBateria?: TipoBateriaCorpus;
    corpusTipo?: CorpusTipoMedia;
    proceso?: string;
    edad?: number;
    estado?: EstadoCorpus;
    sinImagen?: boolean;
    sinAudio?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ListaCorpus>("/v1/word-bank/admin/word-bank", {
      query: {
        ...(q?.tipoBateria ? { tipoBateria: q.tipoBateria } : {}),
        ...(q?.corpusTipo ? { corpusTipo: q.corpusTipo } : {}),
        ...(q?.proceso ? { proceso: q.proceso } : {}),
        ...(q?.edad !== undefined ? { edad: q.edad } : {}),
        ...(q?.estado ? { estado: q.estado } : {}),
        ...(q?.sinImagen ? { sinImagen: "true" } : {}),
        ...(q?.sinAudio ? { sinAudio: "true" } : {}),
        ...(q?.limit !== undefined ? { limit: q.limit } : {}),
        ...(q?.offset !== undefined ? { offset: q.offset } : {}),
      },
    }),
  wordBankCorpusItem: (id: string) =>
    api.get<CorpusItemDetalle>(`/v1/word-bank/admin/word-bank/${encodeURIComponent(id)}`),
  // Medios de estímulo por ítem del corpus (:tipo/:ref = corpusTipo / uuid)
  medios: (tipo: CorpusTipoMedia, ref: string) =>
    api.get<MediosDeItem>(`/v1/word-bank/admin/word-bank/${tipo}/${ref}/media`),
  subirMedio: (tipo: CorpusTipoMedia, ref: string, form: FormData) =>
    api.postForm<WordMediaItem>(`/v1/word-bank/admin/word-bank/${tipo}/${ref}/media`, form),
  eliminarMedio: (tipo: CorpusTipoMedia, ref: string, mediaId: string) =>
    api.del<void>(`/v1/word-bank/admin/word-bank/${tipo}/${ref}/media/${mediaId}`),
  // Licencias — gestión admin sobre M10 real
  licencias: (q?: {
    estadoLicencia?: EstadoLicencia;
    estadoSuscripcion?: EstadoSuscripcion;
    terapeutaId?: string;
    porVencer?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ListaLicencias>("/v1/billing/admin/licenses", {
      query: {
        ...(q?.estadoLicencia ? { estadoLicencia: q.estadoLicencia } : {}),
        ...(q?.estadoSuscripcion ? { estadoSuscripcion: q.estadoSuscripcion } : {}),
        ...(q?.terapeutaId ? { terapeutaId: q.terapeutaId } : {}),
        ...(q?.porVencer ? { porVencer: "true" } : {}),
        ...(q?.limit !== undefined ? { limit: q.limit } : {}),
        ...(q?.offset !== undefined ? { offset: q.offset } : {}),
      },
    }),
  licenciaDetalle: (id: string) =>
    api.get<LicenciaAdminDetalle>(`/v1/billing/admin/licenses/${id}`),
  suspenderLicencia: (id: string) =>
    api.post<LicenciaAdminDetalle>(`/v1/billing/admin/licenses/${id}/suspend`),
  reactivarLicencia: (id: string) =>
    api.post<LicenciaAdminDetalle>(`/v1/billing/admin/licenses/${id}/reactivate`),
  // Planes (products) — CRUD + versionado. codigo auto-generado en backend.
  planes: () => api.get<ProductoAdmin[]>("/v1/billing/admin/products"),
  crearPlan: (b: CrearPlanInput) => api.post<ProductoAdmin>("/v1/billing/admin/products", b),
  editarPlan: (id: string, b: EditarPlanInput) =>
    api.patch<ProductoAdmin>(`/v1/billing/admin/products/${id}`, b),
  duplicarPlan: (id: string, b: DuplicarPlanInput) =>
    api.post<ProductoAdmin>(`/v1/billing/admin/products/${id}/duplicar`, b),
  // Créditos de evaluación de un usuario (saldo + ledger) y ajuste manual.
  creditos: (usuarioId: string, q?: { limit?: number; offset?: number }) =>
    api.get<LedgerCreditos>(`/v1/billing/admin/credits/${usuarioId}`, { query: { ...q } }),
  ajustarCreditos: (usuarioId: string, delta: number, motivo: string) =>
    api.post<SaldoCreditos>("/v1/billing/admin/credits/adjust", { usuarioId, delta, motivo }),
  // Asignar créditos (vía rápida universal, padre o terapeuta) — multipart con
  // comprobante OPCIONAL (obligatorio si justificacionTipo=pago_por_transferencia).
  asignarCreditos: (form: FormData) =>
    api.postForm<AsignacionCredito>("/v1/billing/admin/credits/assign", form),
  asignaciones: (q?: {
    usuarioId?: string;
    justificacionTipo?: JustificacionAsignacion;
    asignadoPor?: string;
    limit?: number;
    offset?: number;
  }) =>
    api.get<ListaAsignaciones>("/v1/billing/admin/credits/assignments", {
      query: {
        ...(q?.usuarioId ? { usuarioId: q.usuarioId } : {}),
        ...(q?.justificacionTipo ? { justificacionTipo: q.justificacionTipo } : {}),
        ...(q?.asignadoPor ? { asignadoPor: q.asignadoPor } : {}),
        ...(q?.limit !== undefined ? { limit: q.limit } : {}),
        ...(q?.offset !== undefined ? { offset: q.offset } : {}),
      },
    }),
  // Pagos offline (transferencia/depósito/Yape/Plin/efectivo). El registro
  // activa la licencia; la anulación puede revertirla (auditado).
  pagosOffline: (q?: {
    metodo?: MetodoPagoOffline;
    desde?: string;
    hasta?: string;
    terapeuta?: string;
    estado?: EstadoPagoOffline;
    limit?: number;
    offset?: number;
  }) => api.get<ListaPagosOffline>("/v1/billing/admin/payments/offline", { query: { ...q } }),
  registrarPagoOffline: (form: FormData) =>
    api.postForm<PagoOffline>("/v1/billing/admin/payments/offline", form),
  anularPagoOffline: (id: string, motivo: string) =>
    api.post<PagoOffline>(`/v1/billing/admin/payments/offline/${id}/anular`, { motivo }),
  // Configuración global
  config: () => api.get<ConfigAdmin>("/v1/config/admin/config"),
  actualizarConfig: (patch: ActualizarConfigInput) =>
    api.patch<ConfigAdmin>("/v1/config/admin/config", patch),
};

// ---- word-bank ------------------------------------------------------------
export const wordBankApi = {
  palabras: (q?: { edadMaxima?: number; segmento?: string; posicion?: Posicion }) =>
    api.get<Palabra[]>("/v1/word-bank/words", { query: { ...q } }),
  palabra: (id: string) => api.get<Palabra>(`/v1/word-bank/words/${id}`),
  pruebas: () => api.get<Prueba[]>("/v1/word-bank/tests"),
  prueba: (id: string) => api.get<PruebaConItems>(`/v1/word-bank/tests/${id}`),
  crearPrueba: (b: { nombre: string; tipo: TipoPrueba; palabraIds: string[]; descripcion?: string }) =>
    api.post<PruebaConItems>("/v1/word-bank/tests", b),
  eliminarPrueba: (id: string) => api.del<void>(`/v1/word-bank/tests/${id}`),
  /** Pista semántica (F2/PSF1) y pregunta distractora (PSF2) de los ítems del plan.
   *  POST con los ids en el body: en query, una batería completa (cientos de UUIDs)
   *  generaba una URL de varios KB → 414 URI Too Long. */
  peffApoyos: (wordIds: string[]) =>
    api.post<PeffApoyo[]>("/v1/word-bank/peff/apoyos", { ids: wordIds }),
};

// ---- assessment v2 (batería PEFF) -----------------------------------------
// El modelo v1 (/sessions) quedó fuera del cliente en la fase 4: el reproductor
// administra baterías. reports (M7) sigue leyendo sesiones v1 hasta la fase 5.
export const bateriaApi = {
  crear: (b: {
    childId: string;
    administrationOrder?: AdministrationOrder;
    modoAdaptativoCompleto?: boolean;
    /** Batería de PRUEBA (QA): subset mínimo, sin validez clínica. El backend
     *  la rechaza (403) si no es admin y el entorno no tiene QA_MODE_ENABLED. */
    modoPrueba?: boolean;
  }) => api.post<BateriaCompletaDTO>("/v1/assessment/baterias", b),
  obtener: (id: string) => api.get<BateriaCompletaDTO>(`/v1/assessment/baterias/${id}`),
  micCheck: (id: string) => api.post<BateriaDTO>(`/v1/assessment/baterias/${id}/mic-check`),
  procesar: (id: string) => api.post<BateriaCompletaDTO>(`/v1/assessment/baterias/${id}/process`),
  suspender: (id: string, motivo: MotivoSuspension) =>
    api.post<BateriaDTO>(`/v1/assessment/baterias/${id}/suspend`, { motivo }),
  reanudar: (id: string) => api.post<BateriaDTO>(`/v1/assessment/baterias/${id}/resume`),
  cancelar: (id: string) => api.post<BateriaDTO>(`/v1/assessment/baterias/${id}/cancel`),

  iniciarEjercicio: (ejercicioId: string) =>
    api.post<EjercicioDTO>(`/v1/assessment/ejercicios/${ejercicioId}/start`),
  completarEjercicio: (ejercicioId: string) =>
    api.post<EjercicioDTO>(`/v1/assessment/ejercicios/${ejercicioId}/complete`),
  descartarEjercicio: (ejercicioId: string) =>
    api.post<EjercicioDTO>(`/v1/assessment/ejercicios/${ejercicioId}/discard`),

  registrarItem: (itemId: string, b: RegistroItem) =>
    api.patch<ItemDTO>(`/v1/assessment/items/${itemId}`, b),

  /** Historial compacto de baterías de un niño (dashboard/historial). */
  listar: (childId: string) =>
    api.get<BateriaResumen[]>("/v1/assessment/baterias", { query: { childId } }),

  /** Vista GLOBAL cross-paciente del terapeuta (propios + vinculados; filtra la
   *  RLS). Paginado (limit/offset), orden por fecha desc. */
  listarGlobal: (q: FiltrosBateriaGlobal = {}) =>
    api.get<ListaBateriasGlobal>("/v1/assessment/baterias/global", {
      query: {
        ...(q.childId ? { childId: q.childId } : {}),
        ...(q.estado ? { estado: q.estado } : {}),
        ...(q.banda ? { banda: q.banda } : {}),
        ...(q.modo ? { modo: q.modo } : {}),
        ...(q.conFlags ? { conFlags: "true" } : {}),
        ...(q.desde ? { desde: q.desde } : {}),
        ...(q.hasta ? { hasta: q.hasta } : {}),
        ...(q.limit !== undefined ? { limit: q.limit } : {}),
        ...(q.offset !== undefined ? { offset: q.offset } : {}),
      },
    }),
  /** Contadores accionables de la cabecera de Evaluaciones (scoped por RLS). */
  statsGlobal: () => api.get<BateriaGlobalStats>("/v1/assessment/baterias/global/stats"),
};

// ---- salud / config público (GET /v1/health, sin auth) --------------------
export interface HealthInfo {
  status: string;
  buildSha: string;
  engineVersion: string;
  inventoryVersion: string;
  /** Modo prueba (QA) habilitado en el entorno: la UI muestra el toggle. */
  qaModeEnabled: boolean;
  ts: string;
}
export const healthApi = {
  obtener: () => api.get<HealthInfo>("/v1/health"),
};

// ---- clinical-review (M11) ------------------------------------------------
// Mesa de revisión clínica: el terapeuta re-examina la batería procesada,
// corrige por ítem, guarda un borrador de validación y FIRMA. Una vez firmado,
// el informe del padre pasa de "pre-informe" a "validado".
export const clinicalApi = {
  workbench: (bateriaId: string) =>
    api.get<Workbench>(`/v1/clinical-review/baterias/${bateriaId}/workbench`),
  corregir: (bateriaId: string, itemId: string, b: CorregirItemInput) =>
    api.post<CorreccionItem>(
      `/v1/clinical-review/baterias/${bateriaId}/items/${itemId}/correction`,
      b,
    ),
  guardarValidacion: (
    bateriaId: string,
    b: { observaciones?: string; conclusionProfesional?: string },
  ) => api.put<Validacion>(`/v1/clinical-review/baterias/${bateriaId}/validation`, b),
  firmar: (bateriaId: string) =>
    api.post<Validacion>(`/v1/clinical-review/baterias/${bateriaId}/sign`),
  informe: (bateriaId: string, variante: Variante) =>
    api.get<Informe>(`/v1/clinical-review/baterias/${bateriaId}/informe`, {
      query: { variante },
    }),
  /** Estado de firma M11 de un LOTE de baterías (hidrata la vista global sin
   *  N+1). Solo devuelve las que tienen validación; ausente = sin firma. */
  estadoValidaciones: (bateriaIds: string[]) =>
    api.post<EstadoValidacion[]>("/v1/clinical-review/validaciones/estado", { bateriaIds }),
};

// ---- shares (M4 identity): compartir pre-informe con un terapeuta ----------
export const shareApi = {
  compartir: (b: { bateriaId: string; childId: string; terapeutaId: string }) =>
    api.post<Compartida>("/v1/identity/shares", b),
  pendientes: () => api.get<Compartida[]>("/v1/identity/shares/pending"),
  aceptar: (id: string) => api.post<Vinculo>(`/v1/identity/shares/${id}/accept`),
  rechazar: (id: string) => api.post<Compartida>(`/v1/identity/shares/${id}/reject`),
};

// ---- media ----------------------------------------------------------------
export const mediaApi = {
  urlSubidaAudio: (b: { childId: string; sesionId: string; palabraId: string; contentType?: string }) =>
    api.post<SubidaAudio>("/v1/media/audio/uploads", b),
  urlAudio: (ref: string) => api.get<{ url: string }>("/v1/media/audio/url", { query: { ref } }),
  protocoloFotos: () => api.get<{ tomas: TipoFoto[] }>("/v1/media/fotos/protocolo"),
  urlSubidaFoto: (b: { childId: string; tipo: TipoFoto; contentType?: string; capturadoEn?: string }) =>
    api.post<SubidaFoto>("/v1/media/fotos/uploads", b),
};

// ---- reports --------------------------------------------------------------
export const reportsApi = {
  generar: (sesionId: string, variante: Variante) =>
    api.get<Reporte>(`/v1/reports/sessions/${sesionId}`, { query: { variante } }),
};

// ---- treatment ------------------------------------------------------------
export const treatmentApi = {
  generarPlan: (b: { evaluacionId: string; umbralTolerante?: number; edadMaxima?: number }) =>
    api.post<PlanCompleto>("/v1/treatment/plans", b),
  plan: (id: string) => api.get<PlanCompleto>(`/v1/treatment/plans/${id}`),
  planesPorNino: (childId: string) =>
    api.get<Plan[]>(`/v1/treatment/children/${childId}/plans`),
  iniciarPractica: (planId: string) =>
    api.post<SesionPractica>(`/v1/treatment/plans/${planId}/practice`),
  modeloGrupo: (practicaId: string, groupId: string) =>
    api.get<ModeloGrupo>(`/v1/treatment/practice/${practicaId}/groups/${groupId}/model`),
  intentar: (
    practicaId: string,
    planWordId: string,
    b: { fonemasObjetivo: string[]; fonemasProducidos: string[] },
  ) => api.post<IntentoResultado>(`/v1/treatment/practice/${practicaId}/words/${planWordId}/attempt`, b),
  finalizar: (practicaId: string) =>
    api.post<SesionPractica>(`/v1/treatment/practice/${practicaId}/finalize`),
  progreso: (childId: string, planId?: string) =>
    api.get<PuntoSerieProgreso[]>(`/v1/treatment/children/${childId}/progress`, {
      query: { ...(planId ? { planId } : {}) },
    }),
};

// ---- licensing-billing ----------------------------------------------------
export const billingApi = {
  productos: () => api.get<Product[]>("/v1/billing/products"),
  suscripcion: () => api.get<EstadoSuscripcionTerapeuta>("/v1/billing/subscription"),
  checkout: (b: { productCodigo: string; payerEmail?: string; backUrl?: string }) =>
    api.post<Checkout>("/v1/billing/checkout", b),
  cancelar: (id: string) => api.post<unknown>(`/v1/billing/subscriptions/${id}/cancel`),
  /** Compra de un paquete por pago único (padre y terapeuta). Devuelve la URL de
   *  checkout MP a la que redirigir; la acreditación llega por webhook. */
  comprar: (b: { productCodigo: string; backUrl?: string }) =>
    api.post<CompraIniciada>("/v1/billing/purchase", b),
  /** Saldo de evaluaciones del usuario autenticado ("Te quedan X"). */
  misCreditos: () => api.get<SaldoCreditos>("/v1/billing/credits"),
};

// ---- marketplace ----------------------------------------------------------
export const marketplaceApi = {
  buscar: (q?: { pais?: string; ciudad?: string; especialidad?: string }) =>
    api.get<PerfilPublico[]>("/v1/marketplace/therapists", { query: { ...q }, auth: false }),
  ficha: (id: string) => api.get<PerfilConContacto>(`/v1/marketplace/therapists/${id}`),
  miPerfil: () => api.get<PerfilPropio>("/v1/marketplace/profile"),
  guardarPerfil: (b: {
    nombreProfesional: string;
    pais: string;
    ciudad: string;
    especialidades?: string[];
    bio?: string;
    contactoEmail?: string;
    contactoWhatsapp?: string;
  }) => api.put<PerfilPropio>("/v1/marketplace/profile", b),
  publicar: () => api.post<PerfilPropio>("/v1/marketplace/profile/publish"),
  despublicar: () => api.post<PerfilPropio>("/v1/marketplace/profile/unpublish"),
  enviarVerificacion: (b: { pais: string; numeroColegiatura: string; documentoRef?: string }) =>
    api.post<Verificacion>("/v1/marketplace/verifications", b),
  misVerificaciones: () => api.get<Verificacion[]>("/v1/marketplace/verifications"),
};
